import { AxiosError } from 'axios'
import { KeyboardEvent, useCallback, useEffect, useState } from 'react'
import BottomSheet from '~components/ui/bottom-sheet'
import Button from '~components/ui/button'
import TextButton from '~components/ui/button/text-button'
import Otp from '~components/ui/text-input/otp'
import TextField from '~components/ui/text-input/text-field'
import useAuthCode, { LOCAL_NUMBER } from '~hooks/use-auth-code'
import useUpdateMyProfile from '~repositories/hooks/use-update-my-profile'
import { getFormattedNumber } from '~utils/string'

type Props = Readonly<{
  open: boolean
  onClose: () => void
}>

const AuthenticationCodeLength = 6

export const AdditionalContactInfoBottomSheet = ({ open, onClose }: Props) => {
  const [step, setStep] = useState<0 | 1>(0)
  const [phoneNumber, setPhoneNumber] = useState('')
  const {
    getCode,
    tokenVerifyingCode,
    authCode,
    timer,
    handleAuthCode,
    handleSendAgain,
    isResendClickable,
  } = useAuthCode()
  const formattedPhoneNumber = getFormattedNumber(phoneNumber)

  const _onClose = useCallback(() => {
    setStep(0)
    onClose()
  }, [onClose])

  const { mutate: updateMyProfile, error } = useUpdateMyProfile({
    onMutate: () => {
      handleAuthCode('')
    },
    onSuccess: () => {
      _onClose()
    },
  })

  const handlePhoneNumber = useCallback(
    (value: string) => {
      const result = value.replace(/^[^0-9]|[^0-9]/g, '')

      if (result.length > 10) {
        return
      }

      setPhoneNumber(result)
    },
    [setPhoneNumber]
  )

  const isGettingCodeDisabled = phoneNumber.length < 10

  const goNextPage = useCallback(async () => {
    if (isGettingCodeDisabled) {
      return
    }
    await getCode({ value: phoneNumber })
    setStep(1)
  }, [isGettingCodeDisabled, getCode, phoneNumber])

  const onEnterPress = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        goNextPage()
      }
    },
    [goNextPage]
  )

  const errorMessage = (error as AxiosError<{ error: { message: string } }>)?.response?.data?.error
    ?.message

  useEffect(() => {
    const trimmedAuthCode = authCode.replaceAll(' ', '')
    if (trimmedAuthCode.length >= AuthenticationCodeLength && tokenVerifyingCode) {
      void updateMyProfile({
        token: tokenVerifyingCode,
        code: authCode,
        phone_number: `${LOCAL_NUMBER}${phoneNumber}`,
      })
    }
  }, [authCode, phoneNumber, tokenVerifyingCode, updateMyProfile])

  return (
    <BottomSheet isOpen={open} onCloseRequest={_onClose} detent="full-height">
      {step === 0 && (
        <>
          <div className="p-5">
            <p className="whitespace-pre-line text-gray-900 text-headline-3 dark:text-white">{`Please enter\nyour phone number`}</p>
          </div>
          <div className="px-5">
            <p className="whitespace-pre-line text-gray-500 text-body-2">{`We’ll text you as soon as your answer is ready`}</p>
          </div>
          <div className="p-5">
            <TextField
              label="Phone number"
              className="w-full"
              placeholder="(123) 456-7890"
              value={formattedPhoneNumber}
              onChange={e => {
                handlePhoneNumber(e.target.value)
              }}
              onKeyDown={onEnterPress}
              type="tel"
              showHelper
            />
            <Button
              className="my-6 w-full lg:mb-4 dark:disabled:bg-gray-800 dark:disabled:text-gray-700"
              size="large"
              disabled={isGettingCodeDisabled}
              onClick={goNextPage}
            >
              Send code
            </Button>
          </div>
        </>
      )}
      {step === 1 && (
        <>
          <div className="p-5">
            <p className="whitespace-pre-line text-gray-900 text-headline-3 dark:text-white">{`Enter the code`}</p>
          </div>
          <div className="px-5">
            <p className="whitespace-pre-line text-gray-500 text-body-2">{`We sent you a verification code to\n${formattedPhoneNumber}`}</p>
          </div>
          <div className="p-5">
            <Otp value={authCode} onChange={handleAuthCode} />
            {errorMessage && <p className="mt-2 text-red-500 text-body-3">{errorMessage}</p>}
            <span className="self-start lg:self-center">
              <TextButton
                size="large"
                disabled={!isResendClickable}
                onClick={() => {
                  if (isResendClickable) {
                    handleSendAgain(phoneNumber)
                  }
                }}
                className="dark:disabled:text-gray-700 dark:text-gray-100"
              >
                Resend code {!isResendClickable ? `${timer.seconds}s` : null}
              </TextButton>
            </span>
          </div>
        </>
      )}
    </BottomSheet>
  )
}
